import type { EntityListEntry } from './EntityListPanel';
import { getCarNodeLabelValues } from './lib/carNodeUtils';

// Helper function to parse coordinates safely
export const parseCoordinates = (properties: any): [number, number] | null => {
  if (!properties) return null;
  
  if (typeof properties === 'object') {
    const lat = parseFloat(properties.latitude);
    const lng = parseFloat(properties.longitude);
    if (!isNaN(lat) && !isNaN(lng)) {
      return [lat, lng];
    }
  }
  return null;
};

// Helper function to format address text
const formatAddress = (properties: any): string => {
  const parts: string[] = [];
  
  if (properties.street) parts.push(properties.street);
  if (properties.postal_address) parts.push(properties.postal_address);
  if (properties.postal_code) parts.push(properties.postal_code);
  
  return parts.length > 0 ? parts.join(', ') : 'Address details unavailable';
};

const toDisplayString = (value: unknown): string | undefined => {
  if (typeof value !== 'string' && typeof value !== 'number') return undefined;
  const text = String(value).replace(/\s+/g, ' ').trim();
  return text || undefined;
};

const getCompanyName = (node: any): string => toDisplayString(
  node.properties?.organization_name || node.name || node.label || node.properties?.organization_number,
) || String(node.id);

export interface ProcessedGraphEntities {
  companies: EntityListEntry[];
  addresses: EntityListEntry[];
  cars: EntityListEntry[];
}

export const processGraphEntities = (nodes: any[], relationships: any[]): ProcessedGraphEntities => {
  // Helper to count connections
  const countConnections = (nodeId: string) => {
    return relationships.filter((rel: any) => String(rel.from) === String(nodeId) || String(rel.to) === String(nodeId)).length;
  };

  const companies: EntityListEntry[] = [];
  const addresses: EntityListEntry[] = [];
  const cars: EntityListEntry[] = [];
  const nodeById = new Map(nodes.map(node => [String(node.id), node]));

  nodes.forEach(node => {
    if (node.type === 'Company') {
      companies.push({
        id: node.id,
        name: getCompanyName(node),
        type: 'Company',
        connectionCount: countConnections(node.id),
        next_layer_company_count: node.properties?.next_layer_company_count,
        data: node
      });
    } else if (node.type === 'Address') {
      addresses.push({
        id: node.id,
        name: formatAddress(node.properties),
        type: 'Address',
        connectionCount: countConnections(node.id),
        coordinates: parseCoordinates(node.properties),
        data: node
      });
    } else if (node.type === 'CarRegNumber') {
      const labelValues = getCarNodeLabelValues(node);
      const connectedCompanyNames = relationships
        .filter((relationship: any) => (
          String(relationship.from) === String(node.id)
          || String(relationship.to) === String(node.id)
        ))
        .map((relationship: any) => {
          const connectedNodeId = String(relationship.from) === String(node.id)
            ? relationship.to
            : relationship.from;
          return nodeById.get(String(connectedNodeId));
        })
        .filter((connectedNode: any) => connectedNode?.type === 'Company')
        .map(getCompanyName)
        .filter((companyName: string, index: number, values: string[]) => values.indexOf(companyName) === index)
        .sort((a: string, b: string) => a.localeCompare(b));

      cars.push({
        id: node.id,
        name: toDisplayString(labelValues.registrationNumber) || String(node.id),
        type: 'CarRegNumber',
        connectionCount: countConnections(node.id),
        connectedCompanyNames,
        manufacturer: toDisplayString(labelValues.manufacturer),
        model: toDisplayString(labelValues.model),
        modelYear: toDisplayString(labelValues.modelYear),
        leasingCompany: toDisplayString(labelValues.leasingCompany),
        data: node,
      });
    }
  });

  // Sort by connection count descending
  companies.sort((a, b) => b.connectionCount - a.connectionCount);
  addresses.sort((a, b) => b.connectionCount - a.connectionCount);
  cars.sort((a, b) => a.name.localeCompare(b.name));

  return { companies, addresses, cars };
};
